import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleAdsAnimated } from './GoogleAdsAnimated';
import { RetailAds } from './RetailAds';
import { useIsForegroundNow } from 'shared/src/hooks';
import { useRetailAds } from '../../queries/useRetailAds';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';

export const AdsContainer = () => {
  const { data: retailAds, refetch } = useRetailAds(201, { enabled: false });
  const isForeground = useIsForegroundNow();
  const [refreshKey, setRefreshKey] = useState(`${Date.now()}`);
  const [showGoogleAds, setShowGoogleAds] = useState<boolean | null>(null);
  const [googleAdsError, setGoogleAdsError] = useState(false);
  const lastFetchTime = useRef(0);
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const fetchAndUpdateAds = useCallback(async () => {
    // 1000ms 이내 중복 호출 방지
    const now = Date.now();
    if (now - lastFetchTime.current < 1000) return;
    lastFetchTime.current = now;

    logAnalyticsEvent({
      name: 'CALL_ADS',
      channel: ['firebase', 'ablylog'],
      analytics: {
        AD_SLOT_ID: 201,
        ADVERTISING_SUPPLIER: 'ABLY_OUTLINK',
      },
    });

    try {
      const { data } = await refetch();
      const adInfo = data?.ad_info;
      const shouldShowGoogleAds = !adInfo;

      // ad_info 가 없으면 GoogleAdsAnimated 가 LOAD_ADS 를 따로 쏘므로 여기선 생략
      if (data != null && adInfo != null) {
        logAnalyticsEvent({
          name: 'LOAD_ADS',
          channel: ['firebase', 'ablylog'],
          analytics: {
            STATUS: data.ad_response_type,
            AD_ID: adInfo.ad_executable_id,
            AD_PLACEMENT_ID: adInfo.ad_placement_id,
            ADVERTISING_SUPPLIER: adInfo.ad_supplier,
            AD_NAME: adInfo.ad_executable_name,
            CREATIVE_TYPE: adInfo.creative_type,
            LINK: adInfo.creative_link,
          },
        });
      }

      setShowGoogleAds(shouldShowGoogleAds);
      setGoogleAdsError(false);

      if (shouldShowGoogleAds) {
        setRefreshKey(`${Date.now()}`);
      }
    } catch {
      logAnalyticsEvent({
        name: 'LOAD_ADS',
        channel: ['firebase', 'ablylog'],
        analytics: {
          STATUS: -1,
          ADVERTISING_SUPPLIER: 'ABLY_OUTLINK',
        },
      });
    }
  }, [refetch, logAnalyticsEvent]);

  // 최초 마운트 시 호출
  useEffect(() => {
    fetchAndUpdateAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // viewDidAppear 시 호출 (중복 호출은 fetchAndUpdateAds 내부에서 debounce)
  useEffect(() => {
    if (isForeground) {
      fetchAndUpdateAds();
    }
  }, [isForeground, fetchAndUpdateAds]);

  // API 응답 대기 중
  if (showGoogleAds === null) {
    return null;
  }

  // GoogleAds 에러 시 아무것도 안 보임
  if (showGoogleAds && googleAdsError) {
    return null;
  }

  if (showGoogleAds) {
    return (
      <GoogleAdsAnimated
        refreshKey={refreshKey}
        onError={() => setGoogleAdsError(true)}
      />
    );
  }

  return <RetailAds retailAds={retailAds} />;
};
